// Account detail — full drill-down: freshness + versions, KPI grid, equity/balance and
// drawdown charts (from downsampled metrics), current positions, pending orders, recent
// closed deals, exposure by symbol, by direction, P&L split, account events and the alert
// timeline. Ported from the mockup rvDetail; data comes from the per-account endpoints.

import { useMemo } from 'react';
import * as api from '../api/client';
import type { AccountRow, MetricPoint } from '../types/api';
import { C, eventGlyph, MONO, SEV, type StateToken } from '../lib/tokens';
import { fmtAge, fmtBk, fmtMoney, fmtPctStr, fmtTS, hbTitle } from '../lib/format';
import { subDec, toNum, sumDec } from '../lib/decimal';
import { chipS, mTd, mTh, mono } from '../lib/styles';
import { derive } from '../lib/mappers';
import { exposureBySymbol } from '../lib/exposure';
import { alertsForAccount } from '../lib/selectors';
import { pnlColor } from '../components/common';
import { useData, useServerNow, useUi } from '../store/hooks';
import { navGo, openDetail, uiStore } from '../store/uiStore';
import { useResource } from '../hooks/useResource';

const W = 680;

function priceStr(s: string | null | undefined, digits: number): string {
  const n = toNum(s);
  return n == null ? '—' : n.toFixed(digits);
}

function linePath(values: number[], H: number, pad: number, lo: number, hi: number): string {
  if (values.length === 0) return '';
  const span = hi - lo || 1;
  const xi = (i: number) => (pad + ((W - 2 * pad) * i) / Math.max(1, values.length - 1)).toFixed(1);
  const yi = (v: number) => (pad + (H - 2 * pad) * (1 - (v - lo) / span)).toFixed(1);
  return 'M' + values.map((v, i) => xi(i) + ' ' + yi(v)).join(' L');
}

export function AccountDetail() {
  const detailId = useUi((s) => s.detailId);
  const accounts = useData((s) => s.accounts);
  const account = useData((s) => (detailId ? s.accountsById[detailId] : undefined));
  const alerts = useData((s) => s.alerts);
  const thr = useUi((s) => s.thr);
  const serverNow = useServerNow();

  const positions = useResource(detailId ? (sig) => api.accountPositions(detailId, sig) : null, [detailId, account?.portfolio_seq]);
  const orders = useResource(detailId ? (sig) => api.accountOrders(detailId, sig) : null, [detailId, account?.portfolio_seq]);
  const deals = useResource(detailId ? (sig) => api.accountDeals(detailId, { limit: 12 }, sig) : null, [detailId]);
  const events = useResource(detailId ? (sig) => api.accountEvents(detailId, 12, sig) : null, [detailId]);
  const metrics = useResource(
    detailId
      ? (sig) => {
          const to = new Date(serverNow).toISOString();
          const from = new Date(serverNow - 24 * 3600 * 1000).toISOString();
          return api.accountMetrics(detailId, { from, to, res: '5m' }, sig);
        }
      : null,
    [detailId],
  );

  if (!detailId || !account) {
    return (
      <section data-screen-label="Account detail" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h1 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Account detail</h1>
        <div style={{ fontSize: 12, color: C.dm }}>Pick an account to inspect, or click any row in the Accounts table.</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: 8 }}>
          {accounts.map((a) => (
            <button
              key={a.id}
              onClick={() => uiStore.set({ detailId: a.id })}
              style={{ textAlign: 'left', background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8, padding: '10px 12px', color: C.tx, fontFamily: 'inherit' }}
            >
              <div style={{ fontSize: 12.5, fontWeight: 600 }}>{a.label}</div>
              <div style={{ fontSize: 10.5, color: C.dm, fontFamily: MONO, marginTop: 2 }}>{a.login_masked + ' · ' + a.broker + ' · ' + fmtMoney(a.equity, a.currency)}</div>
            </button>
          ))}
        </div>
      </section>
    );
  }

  const a: AccountRow = account;
  const d = derive(a, serverNow, thr);
  const tzo = a.broker_tz_offset;
  const posList = positions.data?.positions ?? [];
  const ordList = orders.data?.orders ?? [];
  const dealList = deals.data?.deals ?? [];
  const evList = events.data?.events ?? [];
  const points: MetricPoint[] = metrics.data?.points ?? [];

  return (
    <section data-screen-label="Account detail" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <button onClick={() => navGo('accounts')} style={{ background: 'none', border: 'none', color: C.bl, fontSize: 12, padding: 0 }}>
          ← Back to accounts
        </button>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'stretch' }}>
        <div style={{ flex: '2 1 380px', minWidth: 320, background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <h1 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>{a.label}</h1>
          <div style={{ fontSize: 11, color: C.dm, fontFamily: MONO }}>{'login ' + a.login_masked + ' · magic ' + a.magic + ' · ' + a.margin_mode + ' · ' + a.currency}</div>
          <div style={{ fontSize: 11.5, color: C.dm }}>{a.broker + ' — ' + a.server + ' · ' + a.vps_id + ' / ' + a.terminal_id + ' · ' + a.strategy + ' · ' + a.tags.join(', ')}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 2 }}>
            {(
              [
                [d.cm, ''],
                [d.bm, 'broker'],
                [d.rm, 'risk'],
                [d.tm, ''],
                [d.qm, 'data'],
              ] as Array<[StateToken, string]>
            ).map(([tok, prefix], i) => (
              <span key={i} style={chipS(tok[1])}>
                {tok[0]} {prefix ? prefix + ' ' : ''}
                {tok[2]}
              </span>
            ))}
          </div>
        </div>

        <Panel title="Freshness">
          {(
            [
              ['Heartbeat', fmtAge(d.hbAgeMs) + ' ago', hbTitle(d.receivedAtMs, tzo)],
              ['Last quote', d.tr === 'inactive' ? '☾ market closed' : fmtAge(d.qtAgeMs) + ' ago', hbTitle(d.quoteAtMs, tzo)],
              ['Broker time', fmtBk(serverNow, tzo) + ' (GMT' + (tzo >= 0 ? '+' : '') + tzo + ')', 'Server ' + a.server],
              ['Local time', fmtTS(serverNow), 'Browser time'],
              ['Data quality', d.qm[0] + ' ' + d.qm[2], d.dq === 'partial' ? 'History permission missing' : 'Snapshot completeness'],
            ] as Array<[string, string, string]>
          ).map(([k, v, ti]) => (
            <Row key={k} k={k} v={v} ti={ti} />
          ))}
        </Panel>

        <Panel title="Versions & runtime">
          {(
            [
              ['EA', a.ea_version],
              ['MT5 build', String(a.terminal_build)],
              ['Uptime', fmtAge(a.uptime_s * 1000)],
              ['Memory', a.mem_mb + ' MB'],
              ['Restarts', String(a.restarts)],
              ['Terminal', a.terminal_id + ' · ' + a.vps_id],
            ] as Array<[string, string]>
          ).map(([k, v]) => (
            <Row key={k} k={k} v={v} />
          ))}
        </Panel>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 8 }}>
        {kpi('Balance', fmtMoney(a.balance, a.currency), (d.balNum ?? 0) < 0 ? C.rd : undefined)}
        {kpi('Equity', fmtMoney(a.equity, a.currency), (d.eqNum ?? 0) <= 0 ? C.rd : undefined)}
        {kpi('Floating P&L', fmtMoney(a.floating_pl, a.currency, true), pnlColor(d.fltNum))}
        {kpi('Daily P&L', fmtMoney(a.daily_pl, a.currency, true) + (d.dailyPctNum != null ? ' (' + fmtPctStr(a.daily_pct, true) + ')' : ''), pnlColor(d.dailyNum))}
        {kpi('Drawdown', fmtPctStr(a.drawdown_pct), d.ddNum != null && d.ddNum > thr.dC ? C.rd : d.ddNum != null && d.ddNum > thr.dW ? C.am : undefined)}
        {kpi('Margin level', d.mlvlNum == null ? 'Unavailable' : Math.round(d.mlvlNum) + '%', d.mlvlNum == null ? C.gy : d.mlvlNum < thr.mC ? C.rd : d.mlvlNum < thr.mW ? C.am : undefined)}
        {kpi('Free margin', fmtMoney(a.free_margin, a.currency), d.fmgNum == null ? C.gy : undefined)}
        {kpi('Open volume', a.positions_count + ' pos · ' + (d.lotsNum ?? 0).toFixed(2) + ' lots', a.positions_count > 0 ? C.bl : undefined)}
      </div>

      <Charts points={points} account={a} thr={thr} loading={metrics.loading} />

      <Block title="Current positions">
        {posList.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
              <thead>
                <tr>
                  {['Symbol', 'Dir', 'Lots', 'Entry', 'Current', 'SL', 'TP', 'Swap', 'Age', 'P&L'].map((h, i) => (
                    <th key={h} scope="col" style={mTh(i >= 2 && i !== 8 ? 'right' : 'left')}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {posList.map((p) => (
                  <tr key={p.position_id}>
                    <td style={mTd('left', mono())} title={'ticket ' + p.ticket}>{p.symbol}</td>
                    <td style={mTd('left', mono(p.side === 'buy' ? C.bl : C.violet))}>{p.side === 'buy' ? '▲ L' : '▼ S'}</td>
                    <td style={mTd('right', mono())}>{(toNum(p.volume) ?? 0).toFixed(2)}</td>
                    <td style={mTd('right', mono(C.b9))}>{priceStr(p.price_open, p.digits)}</td>
                    <td style={mTd('right', mono(C.b9))}>{priceStr(p.price_current, p.digits)}</td>
                    <td style={mTd('right', mono(p.sl ? C.b9 : C.dim2))}>{priceStr(p.sl, p.digits)}</td>
                    <td style={mTd('right', mono(p.tp ? C.b9 : C.dim2))}>{priceStr(p.tp, p.digits)}</td>
                    <td style={mTd('right', mono((toNum(p.swap) ?? 0) >= 0 ? C.b9 : C.am))}>{fmtMoney(p.swap, a.currency, true)}</td>
                    <td style={mTd('left', mono(C.dm))} title={'Opened ' + fmtTS(Date.parse(p.time_open))}>{fmtAge(serverNow - Date.parse(p.time_open))}</td>
                    <td style={mTd('right', mono(pnlColor(toNum(p.profit))))}>{fmtMoney(p.profit, a.currency, true)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: 16, textAlign: 'center', fontSize: 11.5, color: C.dm }}>{positions.loading ? 'Loading…' : 'Flat — no open positions.'}</div>
        )}
      </Block>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 320px', minWidth: 300, background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8, overflow: 'hidden' }}>
          <BlockHead>Pending orders</BlockHead>
          {ordList.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Type', 'Symbol', 'Lots', 'Price', 'SL / TP'].map((h, i) => (
                    <th key={h} scope="col" style={mTh(i >= 2 && i < 4 ? 'right' : 'left')}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ordList.map((o) => (
                  <tr key={o.ticket}>
                    <td style={mTd('left', mono(C.bl))}>{o.type.replace(/_/g, ' ')}</td>
                    <td style={mTd('left', mono())}>{o.symbol}</td>
                    <td style={mTd('right', mono())}>{(toNum(o.volume) ?? 0).toFixed(2)}</td>
                    <td style={mTd('right', mono(C.b9))}>{priceStr(o.price_open, o.digits)}</td>
                    <td style={mTd('left', mono(C.dm))}>{priceStr(o.sl, o.digits) + ' / ' + priceStr(o.tp, o.digits)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: 14, textAlign: 'center', fontSize: 11.5, color: C.dm }}>{orders.loading ? 'Loading…' : 'No pending orders.'}</div>
          )}
        </div>

        <div style={{ flex: '1.6 1 420px', minWidth: 340, background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8, overflow: 'hidden' }}>
          <BlockHead>Recent closed trades</BlockHead>
          {dealList.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
                <thead>
                  <tr>
                    {['Closed', 'Deal', 'Symbol', 'Dir', 'Lots', 'Price', 'Entry', 'P&L'].map((h, i) => (
                      <th key={h} scope="col" style={mTh(i >= 4 ? 'right' : 'left')}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dealList.map((c) => (
                    <tr key={c.deal_id}>
                      <td style={mTd('left', mono(C.dm))}>{fmtTS(Date.parse(c.time))}</td>
                      <td style={mTd('left', mono(C.dm))}>{c.deal_id}</td>
                      <td style={mTd('left', mono())}>{c.symbol}</td>
                      <td style={mTd('left', mono(c.side === 'buy' ? C.bl : C.violet))}>{c.side === 'buy' ? '▲ L' : '▼ S'}</td>
                      <td style={mTd('right', mono())}>{(toNum(c.volume) ?? 0).toFixed(2)}</td>
                      <td style={mTd('right', mono(C.b9))}>{c.price}</td>
                      <td style={mTd('right', mono(C.dm))}>{c.entry}</td>
                      <td style={mTd('right', mono(pnlColor(toNum(c.profit))))}>{fmtMoney(c.profit, a.currency, true)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: 14, textAlign: 'center', fontSize: 11.5, color: C.dm }}>{deals.loading ? 'Loading…' : 'No trades closed recently.'}</div>
          )}
        </div>
      </div>

      <Exposure account={a} positions={posList} />

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1.4 1 380px', minWidth: 340, background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8, padding: '10px 14px' }}>
          <h2 style={{ margin: '0 0 8px', fontSize: 12.5, fontWeight: 600 }}>Account events</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {evList.length === 0 && <div style={{ fontSize: 11.5, color: C.dm }}>{events.loading ? 'Loading…' : 'No recent events.'}</div>}
            {evList.map((e, i) => {
              const g = eventGlyph(e.kind);
              return (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                  <span style={{ fontFamily: MONO, color: g[1], fontSize: 12, width: 18, textAlign: 'center' }} aria-hidden="true">{g[0]}</span>
                  <span style={{ flex: 1, fontSize: 11.5 }}>{e.label + (e.amount ? ' — ' + fmtMoney(e.amount, a.currency, true) : '')}</span>
                  <span style={{ fontSize: 10, color: C.dm, fontFamily: MONO, whiteSpace: 'nowrap' }}>{fmtTS(Date.parse(e.at)) + ' · ' + fmtAge(serverNow - Date.parse(e.at)) + ' ago'}</span>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 10, color: C.dim2, marginTop: 8 }}>⇩ deposit · ⇧ withdrawal · ⟳ restart · ⊘ broker disconnect · … telemetry gap</div>
        </div>

        <div style={{ flex: '1 1 320px', minWidth: 300, background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8, padding: '10px 14px' }}>
          <h2 style={{ margin: '0 0 8px', fontSize: 12.5, fontWeight: 600 }}>Alert timeline</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {alertsForAccount(alerts, a.id).length === 0 && <div style={{ fontSize: 11.5, color: C.dm }}>No alerts recorded for this account.</div>}
            {alertsForAccount(alerts, a.id).map((al) => {
              const tok = SEV[al.severity]!;
              return (
                <div key={al.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                  <span style={{ fontFamily: MONO, color: tok[1], fontSize: 12, width: 18, textAlign: 'center' }} aria-hidden="true">{tok[0]}</span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 11.5 }}>
                    {al.title}
                    <span style={{ display: 'block', fontSize: 10, color: C.dm }}>{al.state + ' · threshold ' + al.threshold_text + ' · started ' + fmtTS(Date.parse(al.started_at))}</span>
                  </span>
                  <span style={{ fontSize: 10, color: C.dm, fontFamily: MONO, whiteSpace: 'nowrap' }}>{fmtAge(serverNow - Date.parse(al.started_at))} ago</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ fontSize: 10.5, color: C.dim2 }}>
        Funding (deposits &amp; withdrawals) changes balance but is never counted as trading P&amp;L. Data quality: {d.qm[2]}.{' '}
        <button onClick={() => openDetail(a.id)} style={{ background: 'none', border: 'none', color: C.bl, fontSize: 10.5, padding: 0 }}>
          refresh
        </button>
      </div>
    </section>
  );
}

function kpi(k: string, v: string, color?: string) {
  return (
    <div key={k} style={{ background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8, padding: '9px 12px' }}>
      <div style={{ fontSize: 9.5, color: C.dm, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase' }}>{k}</div>
      <div style={{ fontFamily: MONO, fontSize: 14, fontWeight: 600, marginTop: 3, color: color ?? C.tx, fontVariantNumeric: 'tabular-nums' }}>{v}</div>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ flex: '1 1 240px', minWidth: 240, background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8, padding: '10px 14px' }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 10.5, color: C.dm, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase' }}>{title}</h2>
      {children}
    </div>
  );
}

function Row({ k, v, ti }: { k: string; v: string; ti?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '4px 0', borderBottom: '1px solid #16203A' }}>
      <span style={{ fontSize: 11, color: C.dm }}>{k}</span>
      <span style={{ fontSize: 11, fontFamily: MONO, textAlign: 'right' }} title={ti}>{v}</span>
    </div>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8, overflow: 'hidden' }}>
      <BlockHead>{title}</BlockHead>
      {children}
    </div>
  );
}

function BlockHead({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: '10px 14px', borderBottom: '1px solid ' + C.ln2 }}>
      <h2 style={{ margin: 0, fontSize: 12.5, fontWeight: 600 }}>{children}</h2>
    </div>
  );
}

function Charts({ points, account, thr, loading }: { points: MetricPoint[]; account: AccountRow; thr: { dW: number; dC: number }; loading: boolean }) {
  const eq = points.map((p) => toNum(p.equity)).filter((v): v is number => v != null);
  const bal = points.map((p) => toNum(p.balance)).filter((v): v is number => v != null);

  const chart = useMemo(() => {
    if (eq.length < 2) return null;
    const all = eq.concat(bal.length ? bal : []);
    let lo = Math.min(...all);
    let hi = Math.max(...all);
    const rng = hi - lo || Math.abs(hi) || 1;
    lo -= rng * 0.08;
    hi += rng * 0.08;
    const H = 120;
    const pad = 6;
    const eqLine = linePath(eq, H, pad, lo, hi);
    const balNum = bal.length ? bal[bal.length - 1]! : null;
    const balY = balNum != null ? (pad + (H - 2 * pad) * (1 - (balNum - lo) / (hi - lo))).toFixed(1) : null;
    const balLine = balY != null ? 'M' + pad + ' ' + balY + ' L' + (W - pad) + ' ' + balY : '';
    const area = eqLine + ' L' + (W - pad) + ' ' + (H - pad) + ' L' + pad + ' ' + (H - pad) + ' Z';
    // Drawdown series from equity peak.
    let peak = -Infinity;
    const dd = eq.map((v) => {
      peak = Math.max(peak, v);
      return peak > 0 ? ((peak - v) / peak) * 100 : 0;
    });
    const maxDd = Math.max(...dd, 1);
    const H2 = 64;
    const ddLine = 'M' + dd.map((v, i) => (6 + ((W - 12) * i) / (dd.length - 1)).toFixed(1) + ' ' + (4 + (H2 - 8) * (v / maxDd)).toFixed(1)).join(' L');
    return { eqLine, balLine, area, ddLine, maxDd, lo: Math.min(...eq), hi: Math.max(...eq) };
  }, [points]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ flex: '2 1 420px', minWidth: 340, background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8 }}>
        <BlockHead>Equity &amp; balance — 24 h</BlockHead>
        <div style={{ padding: '10px 14px' }}>
          {chart ? (
            <>
              <svg viewBox={`0 0 ${W} 120`} preserveAspectRatio="none" role="img" aria-label="Equity over the last 24 hours" style={{ width: '100%', height: 120, display: 'block' }}>
                <path d={chart.area} fill="rgba(76,158,232,.13)" />
                <path d={chart.balLine} stroke={C.dm} strokeWidth={1} strokeDasharray="4 3" fill="none" vectorEffect="non-scaling-stroke" />
                <path d={chart.eqLine} stroke={C.bl} strokeWidth={1.5} fill="none" vectorEffect="non-scaling-stroke" />
              </svg>
              <div style={{ fontSize: 10.5, color: C.dm, fontFamily: MONO, marginTop: 6 }}>
                24 h · low {fmtMoney(String(chart.lo), account.currency)} · high {fmtMoney(String(chart.hi), account.currency)} · equity solid blue · balance dashed
              </div>
            </>
          ) : (
            <div style={{ fontSize: 11.5, color: C.dm, padding: '30px 0', textAlign: 'center' }}>{loading ? 'Loading metrics…' : 'Historical metrics unavailable for this account.'}</div>
          )}
        </div>
      </div>
      <div style={{ flex: '1 1 300px', minWidth: 280, background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8 }}>
        <BlockHead>Drawdown — 24 h</BlockHead>
        <div style={{ padding: '10px 14px' }}>
          {chart ? (
            <>
              <svg viewBox={`0 0 ${W} 64`} preserveAspectRatio="none" role="img" aria-label="Drawdown over the last 24 hours" style={{ width: '100%', height: 64, display: 'block' }}>
                <path d={chart.ddLine} stroke={C.am} strokeWidth={1.5} fill="none" vectorEffect="non-scaling-stroke" />
              </svg>
              <div style={{ fontSize: 10.5, color: C.dm, fontFamily: MONO, marginTop: 6 }}>max {chart.maxDd.toFixed(1)}% · warn &gt; {thr.dW}% · critical &gt; {thr.dC}%</div>
            </>
          ) : (
            <div style={{ fontSize: 11.5, color: C.dm, padding: '18px 0', textAlign: 'center' }}>{loading ? 'Loading…' : 'Unavailable'}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Exposure({ account, positions }: { account: AccountRow; positions: import('../types/api').Position[] }) {
  const expo = exposureBySymbol(positions);
  const buyLots = sumDec(positions.filter((p) => p.side === 'buy').map((p) => p.volume));
  const sellLots = sumDec(positions.filter((p) => p.side === 'sell').map((p) => p.volume));
  const net = subDec(buyLots, sellLots);
  const realized = subDec(account.daily_pl, account.floating_pl);

  const dv = (k: string, v: string, color?: string) => (
    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '5px 0', borderBottom: '1px solid #16203A' }}>
      <span style={{ fontSize: 11, color: C.dm }}>{k}</span>
      <span style={{ fontFamily: MONO, fontSize: 13, fontWeight: 600, color: color ?? C.tx, fontVariantNumeric: 'tabular-nums' }}>{v}</span>
    </div>
  );

  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ flex: '1.4 1 340px', minWidth: 320, background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8, padding: '10px 14px' }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 12.5, fontWeight: 600 }}>Exposure by symbol</h2>
        {expo.length === 0 ? (
          <div style={{ fontSize: 11.5, color: C.dm }}>No exposure — account is flat.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {expo.map((e) => (
              <div key={e.sym} style={{ display: 'grid', gridTemplateColumns: '64px 1fr 1fr', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 10.5, fontFamily: MONO }}>{e.sym}</span>
                <ExpoBar frac={e.buyFrac} color={C.bl} label={(toNum(e.buyLots) ?? 0).toFixed(2) + ' L'} />
                <ExpoBar frac={e.sellFrac} color={C.violet} label={(toNum(e.sellLots) ?? 0).toFixed(2) + ' S'} />
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{ flex: '1 1 240px', minWidth: 220, background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8, padding: '10px 14px' }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 12.5, fontWeight: 600 }}>By direction</h2>
        {dv('Long', (toNum(buyLots) ?? 0).toFixed(2) + ' lots', C.bl)}
        {dv('Short', (toNum(sellLots) ?? 0).toFixed(2) + ' lots', C.violet)}
        {dv('Net', ((toNum(net) ?? 0) >= 0 ? '+' : '') + (toNum(net) ?? 0).toFixed(2) + ' lots', (toNum(net) ?? 0) >= 0 ? C.bl : C.violet)}
      </div>
      <div style={{ flex: '1 1 260px', minWidth: 240, background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8, padding: '10px 14px' }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 12.5, fontWeight: 600 }}>P&amp;L split</h2>
        {dv('Realized today', fmtMoney(realized, account.currency, true), pnlColor(toNum(realized)))}
        {dv('Unrealized (floating)', fmtMoney(account.floating_pl, account.currency, true), pnlColor(toNum(account.floating_pl)))}
        {dv('Day total', fmtMoney(account.daily_pl, account.currency, true), pnlColor(toNum(account.daily_pl)))}
        <div style={{ fontSize: 10, color: C.dim2, marginTop: 7 }}>Funding is never counted as trading P&amp;L.</div>
      </div>
    </div>
  );
}

function ExpoBar({ frac, color, label }: { frac: number; color: string; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ flex: 1, height: 9, background: '#16223A', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: Math.round(frac * 100) + '%', height: '100%', background: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 9.5, fontFamily: MONO, color: C.dm, minWidth: 50, textAlign: 'right' }}>{label}</span>
    </div>
  );
}
